import React from "react";
import { connect, ConnectedProps } from "react-redux";
import {
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { useNavigate, Outlet } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BuildIcon from "@mui/icons-material/Build";
import FlagIcon from "@mui/icons-material/Flag";
import WorkIcon from "@mui/icons-material/Work";

// Import actions and types
import { GetMenuDetails } from "./EdvisorLoginAction";
import { MenuItem, LoginResponse, EdvisorLoginState } from "./EdvisorLoginReducer";

// Icon mapping based on menu names - WITH NULL CHECK
const getIconForMenu = (menuName: string | null | undefined): React.ReactElement => {
  if (!menuName) {
    return <DashboardIcon />;
  }
  
  const name = menuName.toLowerCase();

  if (name.includes('employee') || 
      name.includes('hr') || 
      name.includes('human') || 
      name.includes('personnel') ||
      name.includes('staff')) {
    return <PeopleIcon />;
  }
  
  if (name.includes('notice') || name.includes('notification')) return <NotificationsIcon />;
  if (name.includes('information') || name.includes('profile') || name.includes('my')) return <PersonIcon />;
  if (name.includes('leave')) return <AssignmentIcon />;
  if (name.includes('task')) return <WorkIcon />;
  if (name.includes('client') || name.includes('service') || name.includes('customer')) return <BuildIcon />;
  if (name.includes('lead')) return <FlagIcon />;
  if (name.includes('security')) return <SecurityIcon />;
  if (name.includes('setting')) return <SettingsIcon />;

  return <DashboardIcon />;
};

// ─── Teal/Cyan palette (matches login page) ────────────────────────────
const C = {
  teal: "#00d2c8",
  tealLight: "#40e8e0",
  tealDark: "#00a89e",
  cyan: "#00b4d8",
  cyanLight: "#48cae4",
  warmGray: "#7ab0b8",
  border: "rgba(0,210,200,.18)",
  white: "#e8f4f6",
  bg: "#04111a",
  bgMid: "#091e2e",
  bgDeep: "#0d2840",
  cardBg: "rgba(12,28,45,.96)",
  sidebarBg: "rgba(7,18,32,.98)",
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  @keyframes shimmerLine {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes particle {
    0%   { transform: translateY(0) translateX(0); opacity: 0; }
    10%  { opacity: 0.85; }
    90%  { opacity: 0.35; }
    100% { transform: translateY(-110vh) translateX(var(--dx)); opacity: 0; }
  }
  @keyframes orbPulse1 {
    0%,100% { transform: scale(1)    translate(0,0);        opacity:.5; }
    50%      { transform: scale(1.1)  translate(28px,-18px); opacity:.7; }
  }
  @keyframes orbPulse2 {
    0%,100% { transform: scale(1)    translate(0,0);         opacity:.38; }
    50%      { transform: scale(1.08) translate(-22px,15px);  opacity:.55; }
  }
  @keyframes fadeSlideIn {
    from { opacity:0; transform:translateX(-14px); }
    to   { opacity:1; transform:translateX(0);     }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0);    }
  }
  @keyframes iconGlow {
    0%,100% { filter: drop-shadow(0 0  7px rgba(0,210,200,.35)); }
    50%      { filter: drop-shadow(0 0 16px rgba(0,210,200,.8)); }
  }
  .ea-particle {
    position:absolute; border-radius:50%; pointer-events:none;
    background: radial-gradient(circle, rgba(0,220,210,.85) 0%, rgba(0,160,190,.3) 60%, transparent 100%);
    animation: particle var(--dur) var(--delay) ease-in infinite;
  }
`;

const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 2,
  left: Math.random() * 100,
  dur: Math.random() * 12 + 8,
  delay: Math.random() * 10,
  dx: (Math.random() - 0.5) * 110,
}));

const drawerWidth = 270;

// Define RootState interface
interface RootState {
  edvisorLogin: EdvisorLoginState;
}

// Define props from Redux
interface ReduxProps {
  menuItems: MenuItem[];
  menuLoading: boolean;
  menuError: string | null;
  loginResponse: LoginResponse | null;
}

// Combine with connected props
interface Props extends ReduxProps, PropsFromRedux { }

interface State {
  activeItem: string;
  initialLoadDone: boolean; // Add this to track initial load
}

// Create a wrapper component that provides navigate using hooks
const OTPreAuthorizationPageWithNavigate: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  return <OTPreAuthorizationPage {...props} navigate={navigate} />;
};

class OTPreAuthorizationPage extends React.Component<Props & { navigate: any }, State> {
  constructor(props: Props & { navigate: any }) {
    super(props);
    this.state = {
      activeItem: this.getActiveItemFromPath(),
      initialLoadDone: false
    };
  }

  componentDidMount() {
    this.fetchMenuItems();
    this.restoreUserData();
  }

  restoreUserData = () => {
    const { loginResponse } = this.props;
    if (loginResponse) {
      localStorage.setItem("userId", loginResponse.employeeId?.toString() || "");
      localStorage.setItem("userName", loginResponse.employeeName || "");
      localStorage.setItem("userEmail", loginResponse.email || "");
      
      if (loginResponse.employeeCode) {
        sessionStorage.setItem("employeeCode", loginResponse.employeeCode);
      }
      if (loginResponse.employeeName) {
        sessionStorage.setItem("employeeName", loginResponse.employeeName);
      }
      if (loginResponse.employeeId) {
        sessionStorage.setItem("employeeId", loginResponse.employeeId.toString());
      }
    }
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    
    const { menuItems } = this.props;

    // When menu items change and are not empty, store them in localStorage
    if (prevProps.menuItems !== menuItems && menuItems && menuItems.length > 0) {
      localStorage.setItem("menuItems", JSON.stringify(menuItems));
      
      // Only navigate on initial load, not on every update
      if (!this.state.initialLoadDone) {
        this.navigateToDefaultPage(menuItems);
        this.setState({ initialLoadDone: true });
      }
    }
  }

  navigateToDefaultPage = (menuItems: MenuItem[]) => {
    // Check current path
    const currentPath = window.location.pathname;
    
    // If already on a valid page (not root /authorize), don't redirect
    if (currentPath !== '/authorize' && currentPath !== '/authorize/') {
      return;
    }
    
    // Find notice menu or use first menu
    const noticeMenu = menuItems.find(m =>
      m.MenuName && m.MenuName.toLowerCase().includes("notice")
    );

    if (noticeMenu) {
      this.setState({ activeItem: noticeMenu.MenuName });
      this.props.navigate(noticeMenu.MenuPath, { replace: true });
    } else if (menuItems.length > 0 && menuItems[0].MenuName) {
      this.setState({ activeItem: menuItems[0].MenuName });
      this.props.navigate(menuItems[0].MenuPath, { replace: true });
    }
  };

  fetchMenuItems = () => {
    if (this.props.menuItems && this.props.menuItems.length > 0) {
      return;
    }

    const storedMenuItems = localStorage.getItem("menuItems");
    if (storedMenuItems) {
      try {
        const parsedMenuItems = JSON.parse(storedMenuItems);
        if (parsedMenuItems && parsedMenuItems.length > 0) {
          return;
        }
      } catch (e) {
        console.error("Failed to parse stored menu items", e);
      }
    }

    const userId = localStorage.getItem("userId") || "";
    const userPassword = localStorage.getItem("userPassword") || "";

    if (userId && userPassword) {
      this.props.getMenuDetails({ userID: userId, userPassword });
    } else {
      console.error("User ID or password not found for fetching menu details");
    }
  };

  getActiveItemFromPath = (): string => {
    const path = window.location.pathname;
    const { menuItems } = this.props;

    if (menuItems && menuItems.length > 0) {
      const activeMenuItem = menuItems.find(item => path.includes(item.MenuPath));
      if (activeMenuItem && activeMenuItem.MenuName) {
        return activeMenuItem.MenuName;
      }
    }

    if (path.includes('/notice')) return "Notice";
    if (path.includes('/profile')) return "My Profile";
    if (path.includes('/leave')) return "Leave Management";
    if (path.includes('/tasks')) return "Task Management";
    if (path.includes('/employee')) return "Employee Management";
    if (path.includes('/service')) return "Service Management";
    if (path.includes('/lead')) return "Lead Management";

    return menuItems && menuItems.length > 0 && menuItems[0].MenuName ? menuItems[0].MenuName : "Notice";
  };

  handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();

    this.setState({ activeItem: "Notice", initialLoadDone: false });

    setTimeout(() => {
      window.location.href = "/login";
    }, 100);
  };
  
  handleNavigation = (path: string, text: string) => {
    this.setState({ activeItem: text });
    this.props.navigate(path);
  };

  getTopLevelMenuItems = (): MenuItem[] => {
    const { menuItems } = this.props;
    if (!menuItems || menuItems.length === 0) return [];
    
    const topLevel = menuItems
      .filter(item => (!item.ParentMenuID || item.ParentMenuID === 0) && item.MenuName !== null)
      .sort((a, b) => (a.MenuOrder || 999) - (b.MenuOrder || 999));
    
    return topLevel;
  };

  getChildMenuItems = (parentId: number): MenuItem[] => {
    const { menuItems } = this.props;
    if (!menuItems || menuItems.length === 0) return [];

    return menuItems
      .filter(item => item.ParentMenuID === parentId && item.MenuName !== null)
      .sort((a, b) => (a.MenuOrder || 999) - (b.MenuOrder || 999));
  };

  render() {
    const { activeItem } = this.state;
    const { menuLoading, menuError, loginResponse, menuItems } = this.props;

    // Get user info from multiple sources
    let displayName = loginResponse?.employeeName || localStorage.getItem("userName") || "User";
    let displayEmail = loginResponse?.email || localStorage.getItem("userEmail") || "user@example.com";
    let employeeCode = sessionStorage.getItem("employeeCode") || loginResponse?.employeeCode || "";

    // Try to parse userData as last resort
    if (displayName === "User") {
      try {
        const userData = localStorage.getItem("userData");
        if (userData) {
          const parsed = JSON.parse(userData);
          displayName = parsed.employeeName || displayName;
          displayEmail = parsed.email || displayEmail;
          employeeCode = parsed.employeeCode || employeeCode;
        }
      } catch (e) {
        console.error("Failed to parse userData", e);
      }
    }

    const topLevelMenuItems = this.getTopLevelMenuItems();

    return (
      <>
        <style>{globalStyles}</style>

        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              background: C.sidebarBg,
              borderRight: `1px solid ${C.border}`,
              display: "flex",
              flexDirection: "column",
              boxShadow: "4px 0 30px rgba(0,0,0,.5)",
            },
          }}
        >
          {/* Top shimmer line */}
          <Box
            sx={{
              height: "2px",
              flexShrink: 0,
              background: `linear-gradient(90deg, transparent, ${C.teal}, ${C.cyanLight}, ${C.teal}, transparent)`,
              backgroundSize: "200% auto",
              animation: "shimmerLine 3.5s linear infinite",
            }}
          />

          <Box
            sx={{
              mx: 2,
              height: "1px",
              background: `linear-gradient(90deg,transparent,${C.border},transparent)`,
              flexShrink: 0,
            }}
          />

          {/* Avatar */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              textAlign: "center",
              animation: "fadeSlideIn .5s ease .1s both",
              opacity: 0,
            }}
          >
            <Box
              sx={{ position: "relative", display: "inline-block", mb: 1.5 }}
            >
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  mx: "auto",
                  background: `linear-gradient(135deg, ${C.tealDark}, ${C.cyan})`,
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 700,
                  fontSize: "1.3rem",
                  color: "#041018",
                  boxShadow: `0 4px 18px rgba(0,210,200,.3)`,
                  border: `2px solid rgba(0,210,200,.25)`,
                }}
              >
                {displayName ? displayName.charAt(0).toUpperCase() : "?"}
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 2,
                  right: 2,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: C.teal,
                  border: "2px solid #07121f",
                  boxShadow: `0 0 6px ${C.teal}`,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
                fontSize: "0.9rem",
                color: C.white,
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: "0.74rem",
                color: C.warmGray,
                mt: 0.3,
              }}
            >
              {displayEmail}
            </Typography>
            {employeeCode && (
              <Typography
                sx={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: "0.7rem",
                  color: C.teal,
                  mt: 0.5,
                  opacity: 0.8,
                }}
              >
                {employeeCode}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              mx: 2,
              height: "1px",
              background: `linear-gradient(90deg,transparent,${C.border},transparent)`,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              color: C.warmGray,
              textTransform: "uppercase",
              px: 3,
              pt: 2.5,
              pb: 1,
            }}
          >
            Navigation
          </Typography>

          <List sx={{ px: 1.5, flex: 1 }}>
            {menuLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={30} sx={{ color: C.teal }} />
              </Box>
            ) : menuError ? (
              <ListItem sx={{ justifyContent: "center", py: 2 }}>
                <Typography sx={{ color: "#ff6b6b", fontSize: "0.85rem", textAlign: "center" }}>
                  {menuError}
                </Typography>
              </ListItem>
            ) : topLevelMenuItems.length === 0 ? (
              <ListItem sx={{ justifyContent: "center", py: 2 }}>
                <Typography sx={{ color: C.warmGray, fontSize: "0.85rem", textAlign: "center" }}>
                  No menu items available
                </Typography>
              </ListItem>
            ) : (
              topLevelMenuItems.map((item, idx) => {
                if (!item.MenuName) return null;
                
                const isActive = activeItem === item.MenuName;
                const childItems = this.getChildMenuItems(item.MenuID);
                const hasChildren = childItems.length > 0;

                return (
                  <React.Fragment key={item.MenuID}>
                    <ListItem
                      disablePadding
                      sx={{
                        mb: 0.5,
                        animation: `fadeSlideIn .4s ease ${0.15 + idx * 0.07}s both`,
                        opacity: 0,
                      }}
                    >
                      <ListItemButton
                        onClick={() => this.handleNavigation(item.MenuPath, item.MenuName)}
                        sx={{
                          borderRadius: "10px",
                          py: 1.3,
                          px: 1.8,
                          background: isActive
                            ? "linear-gradient(135deg, rgba(0,210,200,.14) 0%, rgba(0,180,216,.08) 100%)"
                            : "transparent",
                          border: isActive
                            ? `1px solid rgba(0,210,200,.22)`
                            : "1px solid transparent",
                          boxShadow: isActive
                            ? "0 2px 12px rgba(0,210,200,.1)"
                            : "none",
                          transition: "all .25s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, rgba(0,210,200,.09) 0%, rgba(0,180,216,.05) 100%)",
                            border: `1px solid rgba(0,210,200,.15)`,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActive ? C.teal : C.warmGray,
                            minWidth: 36,
                            filter: isActive
                              ? `drop-shadow(0 0 6px rgba(0,210,200,.45))`
                              : "none",
                            transition: "all .25s ease",
                            "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
                          }}
                        >
                          {getIconForMenu(item.MenuName)}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.MenuName}
                          primaryTypographyProps={{
                            fontFamily: "'DM Sans',sans-serif",
                            fontWeight: isActive ? 600 : 400,
                            fontSize: "0.88rem",
                            color: isActive ? C.white : C.warmGray,
                          }}
                        />
                        {isActive && (
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              background: C.teal,
                              boxShadow: `0 0 6px ${C.teal}`,
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>

                    {hasChildren && (
                      <Box sx={{ pl: 2 }}>
                        {childItems.map((child) => {
                          if (!child.MenuName) return null;
                          
                          const isChildActive = activeItem === child.MenuName;

                          return (
                            <ListItem
                              key={child.MenuID}
                              disablePadding
                              sx={{ mb: 0.5 }}
                            >
                              <ListItemButton
                                onClick={() => this.handleNavigation(child.MenuPath, child.MenuName)}
                                sx={{
                                  borderRadius: "10px",
                                  py: 1,
                                  px: 1.8,
                                  background: isChildActive
                                    ? "linear-gradient(135deg, rgba(0,210,200,.14) 0%, rgba(0,180,216,.08) 100%)"
                                    : "transparent",
                                  border: isChildActive
                                    ? `1px solid rgba(0,210,200,.22)`
                                    : "1px solid transparent",
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    color: isChildActive ? C.teal : C.warmGray,
                                    minWidth: 36,
                                    "& .MuiSvgIcon-root": { fontSize: "1rem" },
                                  }}
                                >
                                  {getIconForMenu(child.MenuName)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.MenuName}
                                  primaryTypographyProps={{
                                    fontFamily: "'DM Sans',sans-serif",
                                    fontWeight: isChildActive ? 600 : 400,
                                    fontSize: "0.82rem",
                                    color: isChildActive ? C.white : C.warmGray,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </Box>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </List>

          <Box sx={{ p: 2, pt: 1 }}>
            <Box
              sx={{
                height: "1px",
                background: `linear-gradient(90deg,transparent,${C.border},transparent)`,
                mb: 2,
              }}
            />
            <Button
              fullWidth
              startIcon={<LogoutIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={this.handleLogout}
              sx={{
                borderRadius: "10px",
                py: 1.2,
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 500,
                fontSize: "0.85rem",
                textTransform: "none",
                letterSpacing: "0.02em",
                color: C.warmGray,
                border: `1px solid ${C.border}`,
                background: "transparent",
                transition: "all .25s ease",
                "&:hover": {
                  color: C.teal,
                  border: `1px solid rgba(0,210,200,.35)`,
                  background: "rgba(0,210,200,.06)",
                  boxShadow: `0 0 16px rgba(0,210,200,.1)`,
                },
              }}
            >
              Sign Out
            </Button>
          </Box>
        </Drawer>

        <Box
          sx={{
            ml: `${drawerWidth}px`,
            minHeight: "100vh",
            position: "relative",
            overflow: "auto",
            background: `linear-gradient(145deg, ${C.bg} 0%, ${C.bgMid} 35%, ${C.bgDeep} 60%, ${C.bg} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: drawerWidth,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "5%",
                right: "10%",
                width: 420,
                height: 420,
                borderRadius: "50%",
                pointerEvents: "none",
                background:
                  "radial-gradient(circle, rgba(0,160,155,.2) 0%, transparent 68%)",
                animation: "orbPulse1 11s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "8%",
                left: "5%",
                width: 480,
                height: 480,
                borderRadius: "50%",
                pointerEvents: "none",
                background:
                  "radial-gradient(circle, rgba(0,120,175,.16) 0%, transparent 68%)",
                animation: "orbPulse2 15s ease-in-out infinite",
              }}
            />

            {PARTICLES.map((p) => (
              <div
                key={p.id}
                className="ea-particle"
                style={{
                  width: p.size,
                  height: p.size,
                  left: `${p.left}%`,
                  bottom: `-${p.size}px`,
                  ["--dur" as any]: `${p.dur}s`,
                  ["--delay" as any]: `${p.delay}s`,
                  ["--dx" as any]: `${p.dx}px`,
                }}
              />
            ))}

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                backgroundImage: `
                linear-gradient(rgba(0,210,200,.022) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,210,200,.022) 1px, transparent 1px)`,
                backgroundSize: "55px 55px",
              }}
            />
          </Box>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              p: 4,
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </>
    );
  }
}

// Map state to props - with localStorage fallback
const mapStateToProps = (state: RootState) => {
  
  let menuItems = state.edvisorLogin?.menuItems || [];
  
  if ((!menuItems || menuItems.length === 0) && typeof window !== 'undefined') {
    const storedMenuItems = localStorage.getItem("menuItems");
    if (storedMenuItems) {
      try {
        menuItems = JSON.parse(storedMenuItems);
      } catch (e) {
        console.error("Failed to parse stored menu items", e);
      }
    }
  }
  
  return {
    menuItems: menuItems,
    menuLoading: state.edvisorLogin?.loading || false,
    menuError: state.edvisorLogin?.error || null,
    loginResponse: state.edvisorLogin?.loginResponse || null,
  };
};

const mapDispatchToProps = {
  getMenuDetails: GetMenuDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(OTPreAuthorizationPageWithNavigate);